/**
 * Article and page body media.
 *
 * 1. Tags each image with its orientation. Landscape images may break out wider
 *    than the text column, portrait images stay inside it — and CSS has no way
 *    to ask an image for its intrinsic ratio, so it has to happen here. Either
 *    way it is a ceiling: an image the editor sized smaller keeps its own size.
 * 2. Marks italic paragraphs that directly follow an image as captions. The
 *    all-italic run is an explicit opt-in from the Shopify editor, so ordinary
 *    body copy after an image is never mistaken for a caption.
 * 3. Drops the alignment the admin rich text editor writes onto an image as an
 *    inline style, which otherwise knocks it out of the layout in
 *    sections/article.liquid. This has to be JS: an inline style outranks any
 *    stylesheet rule, and the one CSS answer — !important on the global
 *    `.rte img` — would also beat the deliberate per-section overrides in
 *    product.liquid and store-event.liquid.
 */

/** The rich text containers that opt into this treatment, each paired with the
    orientation CSS in its own section. Deliberately not every `.rte`: product
    descriptions, policies and rich text blocks keep the global sizing from
    styles.css, where every image breaks out regardless of shape. */
const MEDIA_CONTAINERS = '.article-body, .page-body';

/** Ratio above which an image is treated as landscape. The comparison in
    setOrientation is strict, so a square image stays at text width along with
    everything taller; only an image wider than tall breaks out. */
const LANDSCAPE_RATIO = 1;

/** Inline declarations the editor's alignment and size controls can produce.
    The `margin` shorthand clears all four sides, which is intended — the
    stylesheet owns the block spacing too.

    `width` is deliberately not in the list: the stylesheet now centres an image
    at whatever width it ends up, and caps it with `max-width`, so an inline
    width the editor wrote is a size the merchant chose and is safe to keep.
    `max-width` and `height` still go: the first would cancel the landscape
    break-out, the second would fight `height: auto` and squash the ratio when a
    wide image is capped. */
const EDITOR_STYLE_PROPERTIES = [
  'float',
  'display',
  'height',
  'max-width',
  'margin',
  'margin-left',
  'margin-right',
  'margin-inline',
  'margin-inline-start',
  'margin-inline-end',
];

/** Presentational attributes from pasted or legacy markup. `align="left"` maps
    to a float hint, and no theme rule declares `float` to outrank it. */
const EDITOR_ATTRIBUTES = ['align', 'hspace', 'vspace'];

/** The `width`/`height` attributes are deliberately left alone: author CSS
    already outranks them, and they give the browser an aspect ratio before the
    image loads. */
function normalizeAlignment(img) {
  EDITOR_STYLE_PROPERTIES.forEach((property) => img.style.removeProperty(property));
  EDITOR_ATTRIBUTES.forEach((attribute) => img.removeAttribute(attribute));
}

/** True when the element's only content is the image — the same condition the
    `p:has(> img:only-child)` rules in sections/article.liquid match on. */
function wrapsOnlyImage(el, img) {
  if (el === img) return false;
  if (el.children.length !== 1) return false;
  if (el.textContent.trim() !== '') return false;

  return el.contains(img);
}

function setOrientation(img) {
  if (!img.naturalWidth || !img.naturalHeight) return;
  img.dataset.orientation = img.naturalWidth / img.naturalHeight > LANDSCAPE_RATIO ? 'landscape' : 'portrait';
}

/** The paragraph is a caption only if its entire content is one <em>/<i>. */
function isItalicParagraph(el) {
  if (!el || el.tagName !== 'P') return false;
  if (el.children.length !== 1) return false;

  const only = el.firstElementChild;
  if (only.tagName !== 'EM' && only.tagName !== 'I') return false;

  return el.textContent.trim() === only.textContent.trim();
}

function enhanceContainer(body) {
  body.querySelectorAll('img').forEach((img) => {
    // Needs no image data, so it runs now rather than waiting on `load`.
    normalizeAlignment(img);

    if (img.complete) {
      setOrientation(img);
    } else {
      img.addEventListener('load', () => setOrientation(img), { once: true });
    }

    // Shopify's RTE wraps images in a <p>; walk up to whichever element is the
    // image's block in the body so the caption check looks at the right sibling.
    let block = img;
    while (block.parentElement && block.parentElement !== body) {
      block = block.parentElement;
    }

    // The editor can align the image by aligning the paragraph around it. Only
    // clear it when that paragraph holds nothing else — centred body copy is
    // a deliberate choice and stays as written.
    if (wrapsOnlyImage(block, img)) {
      block.style.removeProperty('text-align');
    }

    if (isItalicParagraph(block.nextElementSibling)) {
      block.nextElementSibling.classList.add('article-caption');
    }
  });
}

/** `root` is the document on load and a section wrapper on section:load, so the
    containers are always descendants — never the root itself. */
function enhanceArticleMedia(root = document) {
  if (!root.querySelectorAll) return;

  root.querySelectorAll(MEDIA_CONTAINERS).forEach(enhanceContainer);
}

// The script tag is deferred, so the DOM is already parsed by the time this
// runs. Waiting on DOMContentLoaded would cost a tick in which a misaligned
// image can paint and then jump.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => enhanceArticleMedia(), { once: true });
} else {
  enhanceArticleMedia();
}

document.addEventListener('shopify:section:load', (event) => enhanceArticleMedia(event.target));
